import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import cogoToast from '@successtar/cogo-toast'
import Button from '@renderer/components/bahela/Button'
import QuitModal from '@renderer/components/bahela/QuitModal'
import Spinner from '@renderer/components/Spinner'
import QrCodeImage from '@renderer/components/QrCodeImage'
import { useInterval } from '@renderer/utils/useInterval'
import MidtransUtils from '@renderer/utils/midtransUtils'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

// Import Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaLogo from '/assets/bahela/bahela.webp'
import qrisId from '/assets/bahela/qris-holder-id.webp'
import qrisEn from '/assets/bahela/qris-holder-en.webp'
import cancelButton from '/assets/bahela/cancel-green.svg'
import paymentFailedId from '/assets/bahela/payment-failed-id.webp'
import paymentFailedEn from '/assets/bahela/payment-failed-en.webp'
import paymentSuccessId from '/assets/bahela/payment-success-id.webp'
import paymentSuccessEn from '/assets/bahela/payment-success-en.webp'
import { qrActivityLogger } from '@renderer/utils/qrLoadReporter'

const PaymentPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  const welcomeSettings = getPageSettings('welcome')
  const paymentSettings = getPageSettings('payment')
  const { transaction, setSessionState, uploadSessionState, logActivity } =
    useSessionStateStore()
  const isMounted = useRef(true)

  // Helper to safely update state/logs only when component is mounted
  const safeUpdate = (callback: () => void): void => {
    if (isMounted.current) {
      callback()
    }
  }

  // States
  const [remainingTime, setRemainingTime] = useState<string | null>(null)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending')

  // Initialize countdown hook
  const paymentCountdown = useCountdown(paymentSettings?.paymentTimeout || 120)

  const cleanupPayment = async (): Promise<void> => {
    if (transaction?.orderId && transaction?.status !== 'settlement') {
      try {
        await fetch(`${process.env.VITE_API_URL}/api/payments/${transaction.orderId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
      } catch (error) {
        console.error('Error cancelling payment:', error)
      }
    }
  }

  useEffect(() => {
    // Upload session state on mount
    uploadSessionState()
    return (): void => {
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      cleanupPayment()
    }
  }, [])

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      const remainingMs = expiryTimestamp - serverTimestamp
      return Math.max(0, Math.floor(remainingMs / 1000))
    } catch (error) {
      console.error('Error calculating remaining time:', error)
      return 0
    }
  }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const continueAfterFreeOrSettled = (reason: string): void => {
    const live = useSessionStateStore.getState()
    setSessionState({
      transaction: {
        status: 'settlement',
        amount: live.transaction?.amount ?? 0
      }
    })
    logActivity('Payment skipped — free or voucher session', {
      reason,
      amount: live.transaction?.amount ?? 0,
      discount: live.transaction?.discount ?? 0,
      voucherCode: live.transaction?.voucherCode
    })
    uploadSessionState()
    const nextPage = welcomeSettings?.startWithPayment ? 'filter' : 'capture'
    navigate(`/${projectId}/${nextPage}`)
  }

  const initiatePayment = async (): Promise<void> => {
    try {
      const live = useSessionStateStore.getState()
      const finalAmount = live.transaction?.amount || 0

      // Cash voucher (already settled) or discount voucher that zeros the cart — never open QRIS.
      if (live.transaction?.status === 'settlement' || finalAmount <= 0) {
        continueAfterFreeOrSettled(
          live.transaction?.status === 'settlement' ? 'already_settled' : 'zero_amount'
        )
        return
      }

      const data = await MidtransUtils.initiatePayment(
        finalAmount,
        live.id || '',
        paymentSettings?.paymentTimeout || 120,
        projectId || '',
        live.userPreferences?.filterId || '',
        live.userPreferences?.frameId || '',
        live.transaction?.discount || 0
      )

      if (data.order_id && data.qr_url) {
        // Sanitize qrUrl to remove double slashes
        const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')

        // Calculate initial remaining time
        const initialRemainingSeconds = calculateRemainingTime(data.expiry_time, data.server_time)

        if (initialRemainingSeconds <= 0) {
          setIsExpired(true)
          return
        }

        setSessionState({
          transaction: {
            orderId: data.order_id,
            qrUrl: sanitizedQrUrl,
            expiryTime: data.expiry_time,
            serverTime: data.server_time,
            status: 'pending',
            amount: finalAmount,
            discount: live.transaction?.discount || 0
          }
        })

        // Log payment start activity
        logActivity('Payment process started', {
          amount: finalAmount,
          discount: live.transaction?.discount || 0,
          orderId: data.order_id,
          expiryTime: data.expiry_time,
          serverTime: data.server_time,
          remainingSeconds: initialRemainingSeconds
        })

        setQrUrl(sanitizedQrUrl)
        setIsExpired(false)

        // Start countdown with initial remaining time
        paymentCountdown.setRemainingSeconds(initialRemainingSeconds)
        paymentCountdown.setOnTick(() => {
          setRemainingTime(formatTime(paymentCountdown.getRemainingSeconds()))
        })
        paymentCountdown.setOnComplete(() => handleCountdownEnd())
        paymentCountdown.start()
        setIsCheckingPayment(true)
      }
    } catch (e: unknown) {
      // Log payment failure activity
      logActivity('Payment process failed', {
        error: (e as Error).message
      })
      console.error('Error initiating payment:', e)
      cogoToast.error((e as Error).message)
    }
  }

  useEffect(() => {
    initiatePayment()

    return (): void => {
      paymentCountdown.stop()
    }
  }, [])

  useInterval(
    async () => {
      await checkPaymentStatus()
    },
    isCheckingPayment ? 5000 : null
  )

  const checkPaymentStatus = async (isButtonClick: boolean = false): Promise<void> => {
    if (!isMounted.current || !transaction?.expiryTime || !transaction?.serverTime) {
      return
    }

    try {
      const paymentData = await MidtransUtils.checkPaymentStatus(
        transaction.orderId || 'order-default-id'
      )

      // If payment has expired, update the UI
      if (paymentData.isExpired) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setIsExpired(true)
          setSessionState({
            transaction: {
              status: 'expire'
            }
          })

          // Log payment timeout activity
          logActivity('Payment timed out', {
            orderId: transaction.orderId,
            expiryTime: transaction.expiryTime,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus
          })

          uploadSessionState()
        })
        return
      }

      if (paymentData.paid) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setPaymentStatus('success')
          setSessionState({
            transaction: {
              status: 'settlement'
            }
          })

          // Log payment success activity
          logActivity('Payment completed successfully', {
            orderId: transaction.orderId,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus
          })

          uploadSessionState()

          // Navigate after showing success screen
          setTimeout(() => {
            const nextPage = welcomeSettings?.startWithPayment ? 'filter' : 'capture'
            navigate(`/${projectId}/${nextPage}`)
          }, 3000)
        })
      } else if (isButtonClick) {
        setPaymentStatus('failed')
        setTimeout(() => {
          setPaymentStatus('pending')
        }, 2000)
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
      if (isButtonClick) {
        safeUpdate(() => {
          cogoToast.error((e as Error).message)
        })
      }
    }
  }

  const handleCountdownEnd = async (): Promise<void> => {
    if (!isMounted.current) {
      return
    }
    await checkPaymentStatus()

    // If the final status check just detected payment, the success path has already
    // set status='settlement' and scheduled navigation to capture. Do NOT clobber it
    // with an expire — otherwise a customer who paid right at the buzzer gets a
    // settlement in the DB but an "expired" screen ("paid but kicked back to start").
    if (useSessionStateStore.getState().transaction?.status === 'settlement') {
      return
    }

    safeUpdate(() => {
      setIsCheckingPayment(false)
      setRemainingTime('00:00')
      setIsExpired(true)
      setSessionState({
        transaction: {
          status: 'expire'
        }
      })

      // Log countdown timeout activity
      logActivity('Payment countdown ended', {
        orderId: transaction?.orderId
      })
    })
  }

  // Call all hooks at the top level (before any conditional returns)
  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedCancelButton = useCachedImage(cancelButton)
  const cachedBahelaLogo = useCachedImage(bahelaLogo)
  const cachedQris = useCachedImage(i18n.language === 'id' ? qrisId : qrisEn)
  const cachedPaymentSuccess = useCachedImage(
    i18n.language === 'id' ? paymentSuccessId : paymentSuccessEn
  )
  const cachedPaymentFailed = useCachedImage(
    i18n.language === 'id' ? paymentFailedId : paymentFailedEn
  )

  // Show success/failed screens
  if (paymentStatus === 'success') {
    return (
      <div className="background relative w-full h-full overflow-hidden">
        <img
          className="w-full h-full object-contain"
          src={cachedPaymentSuccess}
          alt="Payment Success"
        />
      </div>
    )
  }
  if (paymentStatus === 'failed') {
    return (
      <div className="background relative w-full h-full overflow-hidden">
        <img
          className="w-full h-full object-contain"
          src={cachedPaymentFailed}
          alt="Payment Success"
        />
      </div>
    )
  }

  return (
    <div className="background relative bg-prominentBlue w-full h-full overflow-hidden">
      <div
        className="deco-img absolute top-0 left-0 w-full h-full bg-cover z-[0]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      />
      <QuitModal showModal={showModal} setShowModal={setShowModal} />
      <div className="absolute left-[50px] top-[50px] flex flex-col items-center">
        <button type={'button'} onClick={() => setShowModal(true)}>
          <img src={cachedCancelButton} className="w-16 h-16 btn-shadow" alt="Cancel" />
        </button>
        <span className="font-body-bahela text-2xl text-uriYellow">
          {i18n.language === 'id' ? 'Beranda' : 'Homepage'}
        </span>
      </div>
      <img
        src={cachedBahelaLogo}
        alt="Bahela Logo"
        className="absolute z-[1] bottom-[66px] left-[57px] w-[127px] h-[114px]"
      />
      <div className="absolute z-[1] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
        <img src={cachedQris} alt="QRIS Holder" className="w-[800px] h-[800px] object-contain" />
        <div className="absolute inset-0 pt-8 items-center flex flex-col">
          <h2 className="font-serif-bahela text-methadone drop-shadow-2xl text-6xl my-4 mb-8">
            {formatAmount(transaction?.amount ?? 0, projectCurrency)}
          </h2>
          {isExpired ? (
            <div
              className="flex flex-col items-center justify-center gap-6 cursor-pointer transition-all duration-300 hover:scale-105 mt-[100px]"
              onClick={() => navigate(`/${projectId}`)}
            >
              <div className="text-center mt-8 p-12">
                <p className="text-4xl font-serif-bahela text-methadone mb-8">
                  {t('payment.expired')}
                </p>
                <p className="text-2xl font-body-bahela text-methadone/80">
                  {t('payment.restartPrompt')}
                </p>
              </div>
              <Button
                bgColor="white"
                textColor="methadone"
                text-color
                className="text-2xl"
                onClick={() => navigate(`/${projectId}`)}
              >
                {t('payment.restart')}
              </Button>
            </div>
          ) : qrUrl ? (
            // Robust QR renderer: shows a spinner (never flashes alt text) until the image
            // actually loads, and auto-retries a flaky fetch. Mirrors the reprint modal.
            <div className="w-[500px] h-[500px]">
              <QrCodeImage
                qrUrl={qrUrl}
                className="w-[500px] h-[500px] object-contain"
                onEvent={qrActivityLogger(logActivity, 'bahela-payment')}
              />
            </div>
          ) : (
            <div className="w-[500px] h-[500px] flex justify-center items-center">
              <Spinner />
            </div>
          )}
        </div>
      </div>
      <div className="absolute z-[6] right-[127px] bottom-[120px] drop-shadow-2xl flex flex-col items-center">
        <span className="text-[50px] text-white font-serif-bahela">{remainingTime || '...'}</span>
        <Button
          disabled={!qrUrl}
          className={`text-[50px] ${qrUrl ? 'opacity-100' : 'opacity-50'}`}
          textColor="prominentBlue"
          onClick={() => checkPaymentStatus(true)}
        >
          {i18n.language === 'id' ? 'Konfirmasi' : 'Confirm'}
        </Button>
      </div>
    </div>
  )
}

export default PaymentPage
